import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { SharedHeader } from "@/components/SharedHeader";
import RotatingNewsBanner from "@/components/RotatingNewsBanner";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Privacy Policy | AI Customer Support Platform | Call24x7.AI</title>
        <meta name="description" content="Call24x7.AI Privacy Policy. Learn how we protect your data when using our AI customer support platform, AI answering service, and AI receptionist service." />
        <link rel="canonical" href="https://call24x7.ai/privacy-policy" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7.AI" />
        
        {/* Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://call24x7.ai"
          }, {
            "@type": "ListItem",
            "position": 2,
            "name": "Privacy Policy",
            "item": "https://call24x7.ai/privacy-policy"
          }]
        })}
        </script>
      </Helmet>
    <div className="min-h-screen bg-background">
      <RotatingNewsBanner />
      <SharedHeader />
      <div className="container mx-auto px-2 sm:px-4 py-8 max-w-4xl pt-32">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="space-y-8 text-foreground/90">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="leading-relaxed">
              Welcome to Call24x7. We respect your privacy and are committed to protecting your personal data. 
              This privacy policy will inform you about how we handle your personal data when you use our mobile 
              application and website, and tell you about your privacy rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            <p className="leading-relaxed mb-3">We may collect, use, store and transfer different kinds of personal data about you:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Identity Data:</strong> Name, username or similar identifier</li>
              <li><strong>Contact Data:</strong> Email address, telephone numbers</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
              <li><strong>Usage Data:</strong> Information about how you use our app and services</li>
              <li><strong>Communication Data:</strong> Call records, chat logs (for service improvement)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="leading-relaxed mb-3">We use your personal data for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>To provide and maintain our AI phone agent services</li>
              <li>To manage your account and provide customer support</li>
              <li>To improve our services and develop new features</li>
              <li>To communicate with you about updates and changes</li>
              <li>To ensure security and prevent fraud</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Data Security</h2>
            <p className="leading-relaxed">
              We have implemented appropriate security measures to prevent your personal data from being accidentally 
              lost, used, accessed, altered, or disclosed in an unauthorized way. All data transmission is encrypted 
              using industry-standard protocols.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
            <p className="leading-relaxed">
              We will only retain your personal data for as long as necessary to fulfill the purposes we collected 
              it for, including satisfying legal, accounting, or reporting requirements. When we no longer need your 
              data, we will securely delete or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Third-Party Services</h2>
            <p className="leading-relaxed">
              Our app may use third-party services that collect information used to identify you. These services 
              have their own privacy policies addressing how they use such information. We encourage you to review 
              their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Your Rights</h2>
            <p className="leading-relaxed mb-3">Under data protection laws, you have rights including:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Restriction:</strong> Request restriction of processing your data</li>
              <li><strong>Portability:</strong> Request transfer of your data</li>
              <li><strong>Objection:</strong> Object to processing of your personal data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Children's Privacy</h2>
            <p className="leading-relaxed">
              Our services are not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you become aware that a child has provided us with 
              personal data, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Changes to This Privacy Policy</h2>
            <p className="leading-relaxed">
              We may update our privacy policy from time to time. We will notify you of any changes by posting 
              the new privacy policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about this privacy policy or our privacy practices, please contact us at:
            </p>
            <p className="leading-relaxed mt-2">
              Email: hello@call24x7.ai
            </p>
          </section>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy;
